// Zustand auth store — sessionStorage-backed.
// Per architecture/frontend-architecture.md + login-flow-spec.md §6.
//
// Tokens live in sessionStorage (closes with the tab) rather than
// localStorage. Trade-off: a fresh tab requires re-login, but a stolen
// machine that didn't fully log out doesn't leave a persistent token.
// Acceptable for B2B internal-tool ergonomics in Phase 1.

import { create } from 'zustand'

const STORAGE_KEY = 'vysto.auth'

export interface AuthUser {
  id: string
  email: string
  name: string
}

export interface AuthSnapshot {
  accessToken: string
  refreshToken: string
  user: AuthUser
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
  /**
   * Short-lived MFA challenge token from /auth/login. Set when the server
   * returns step:'mfa'; consumed by /auth/mfa/verify in L7.
   */
  mfaChallengeToken: string | null
  /**
   * Short-lived forced-password-change token from /auth/login. Set when the
   * server returns step:'password_change' (direct-created accounts on their
   * one-time temp password); consumed by /auth/password-change.
   */
  passwordChangeToken: string | null

  setSession: (snapshot: AuthSnapshot) => void
  setMfaChallenge: (token: string) => void
  setPasswordChange: (token: string) => void
  clear: () => void
}

function readPersisted(): {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
} {
  if (typeof window === 'undefined') return { accessToken: null, refreshToken: null, user: null }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { accessToken: null, refreshToken: null, user: null }
    const parsed = JSON.parse(raw) as Partial<AuthSnapshot>
    return {
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
      user: parsed.user ?? null,
    }
  } catch {
    return { accessToken: null, refreshToken: null, user: null }
  }
}

function writePersisted(snapshot: AuthSnapshot | null): void {
  if (typeof window === 'undefined') return
  if (snapshot === null) {
    sessionStorage.removeItem(STORAGE_KEY)
    return
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
}

/**
 * Zustand hook holding tokens + user, persisted to sessionStorage (dies with
 * the tab). `setSession` also clears any pending MFA challenge token; outside
 * React, use the `authStore` accessors instead.
 */
export const useAuthStore = create<AuthState>((set) => ({
  ...readPersisted(),
  mfaChallengeToken: null,
  passwordChangeToken: null,

  setSession: (snapshot) => {
    writePersisted(snapshot)
    set({
      accessToken: snapshot.accessToken,
      refreshToken: snapshot.refreshToken,
      user: snapshot.user,
      mfaChallengeToken: null,
      passwordChangeToken: null,
    })
  },

  setMfaChallenge: (token) => {
    set({ mfaChallengeToken: token })
  },

  setPasswordChange: (token) => {
    set({ passwordChangeToken: token })
  },

  clear: () => {
    writePersisted(null)
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      mfaChallengeToken: null,
      passwordChangeToken: null,
    })
  },
}))

/** Non-hook accessors — used by the axios interceptor outside React. */
export const authStore = {
  get: (): AuthState => useAuthStore.getState(),
  setSession: (snapshot: AuthSnapshot): void => {
    useAuthStore.getState().setSession(snapshot)
  },
  clear: (): void => {
    useAuthStore.getState().clear()
  },
}
